import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { fmtDate, fmtMoney } from "@/components/ripeness/radarMeta";
import { fmtRange } from "@/components/ripeness/ValueCell";
import { SourceTypeBadge } from "@/components/ripeness/SourceBadges";

const pct = (x) => (x === null || x === undefined ? "—" : `${Math.round(Number(x) * 100)}%`);

function FactorRow({ k, v, note, testId }) {
    return (
        <div className="flex justify-between gap-3 text-xs py-0.5" data-testid={testId}>
            <span className="text-slate-500">{k}</span>
            <span className="text-right text-slate-200">{v}{note ? <span className="text-slate-500"> · {note}</span> : null}</span>
        </div>
    );
}

function Factors({ f, cur }) {
    const pps = f.patients_per_site;
    const b = f.budget_per_patient;
    const ind = f.indication_factor || { value: f.area_factor?.value, level: "area", area: f.area_factor?.area };
    const loc = f.location_index;
    const ff = f.fixed_fees_detail;
    const bppNote = b.locked ? "manual · locked" : b.n ? `${b.source} · ${b.n} CTAs · ${Math.round(b.weight * 100)} % data` : "manual";
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mt-2 border-t border-slate-800 pt-2" data-testid="dd-value-factors">
            <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">If awarded</div>
                <FactorRow k="Patients / site" v={pps.value} note={pps.note} testId="dd-vf-pps" />
                <FactorRow k="Budget / patient" v={fmtRange(b, cur)} note={bppNote} testId="dd-vf-bpp" />
                {b.phase_range ? <FactorRow k="Phase range" v={fmtRange(b.phase_range, cur)} note={b.data ? `CTA data ${fmtRange(b.data, cur)}` : null} testId="dd-vf-phase-range" /> : null}
                <FactorRow k="Indication factor" v={`× ${ind.value}`} note={ind.level === "indication" ? `${ind.label} (override)` : `${ind.area} (area)`} testId="dd-vf-area" />
                {loc ? <FactorRow k="Location index" v={`× ${Number(loc.value).toFixed(2)}`} note={loc.note} testId="dd-vf-location" /> : null}
                <FactorRow k="Fixed fees" v={fmtMoney(f.fixed_fees, cur)} note={ff ? (ff.locked ? "locked" : ff.n ? `${ff.source} · ${ff.n} CTAs` : `${fmtMoney(ff.base, cur)} × index`) : null} testId="dd-vf-fixed" />
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Expected = if awarded ×</div>
                <FactorRow k="Trial happens" v={pct(f.chance_trial_happens.value)} note={f.chance_trial_happens.label} testId="dd-vf-p" />
                <FactorRow k="Win rate" v={pct(f.win_rate)} testId="dd-vf-win" />
                <FactorRow k="Enrollment achieved" v={pct(f.enrollment_achieved)} testId="dd-vf-enr" />
                <FactorRow k="Window still open" v={pct(f.window_open.value)} note={String(f.window_open.stage || "").replace(/_/g, " ")} testId="dd-vf-window" />
                <FactorRow k="Market fit" v={pct(f.market_fit.value)} note={f.market_fit.reason} testId="dd-vf-fit" />
                <FactorRow k="Multiplier" v={`× ${f.multiplier}`} testId="dd-vf-mult" />
            </div>
        </div>
    );
}

/** Drill-down Value card — one tab per phase the track has data for. */
export function ValueCard({ value, byPhase }) {
    const phases = Object.keys(byPhase || {}).sort();
    const [ph, setPh] = useState(value?.phase || phases[0]);
    useEffect(() => { setPh(value?.phase || phases[0]); }, [value?.phase, phases.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
    const v = (byPhase || {})[ph] || value;
    if (!v) return null;
    const cur = v.currency;
    return (
        <div className="mt-4 rounded-lg border border-emerald-400/20 bg-emerald-400/[0.03] p-3" data-testid="dd-value-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Lead value <span className="text-slate-600 normal-case tracking-normal">· {v.model}</span></div>
                <div className="flex items-center gap-1" data-testid="dd-value-phase-tabs">
                    {phases.map((p) => (
                        <button key={p} type="button" onClick={() => setPh(p)}
                            className={`rounded px-2 py-0.5 text-[11px] border ${p === ph ? "border-emerald-400/60 text-emerald-200 bg-emerald-400/10" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}
                            data-testid={`dd-value-phase-tab-${p}`}>
                            P{p}{String(p) === String(value?.phase) ? " ·best" : ""}
                        </button>
                    ))}
                </div>
            </div>
            {v.expected ? (
                <div className={`grid ${v.weighted && v.weighted.weight !== 1 ? "grid-cols-3" : "grid-cols-2"} gap-4 mt-2`}>
                    <div data-testid="dd-value-expected">
                        <div className="text-2xl font-bold text-emerald-200 leading-tight">{fmtMoney(v.expected.mid, cur)}</div>
                        <div className="text-xs text-slate-400">expected · {fmtRange(v.expected, cur)}</div>
                    </div>
                    {v.weighted && v.weighted.weight !== 1 ? (
                        <div data-testid="dd-value-weighted">
                            <div className="text-2xl font-bold text-violet-200 leading-tight">{fmtMoney(v.weighted.mid, cur)}</div>
                            <div className="text-xs text-slate-400">value to us · ×{Number(v.weighted.weight).toFixed(2)} {v.weighted.level === "indication" ? "indication" : "area"} weight</div>
                        </div>
                    ) : null}
                    <div data-testid="dd-value-awarded">
                        <div className="text-2xl font-semibold text-slate-100 leading-tight">{fmtMoney(v.if_awarded.mid, cur)}</div>
                        <div className="text-xs text-slate-400">if awarded · {fmtRange(v.if_awarded, cur)}</div>
                    </div>
                </div>
            ) : (
                <div className="mt-2 text-sm text-slate-400" data-testid="dd-value-none">— <span className="text-xs text-slate-500">{v.reason_label || "No value for this phase"}</span></div>
            )}
            {v.factors ? <Factors f={v.factors} cur={cur} /> : null}
        </div>
    );
}

/** Every source article with a URL behind the track, newest first. */
export function SourcesList({ sources }) {
    if (!sources?.length) {
        return <div className="mt-3 text-xs text-slate-500 italic" data-testid="dd-sources-empty">No article URL on record for this track (historical records are backfilled nightly where NewsAPI.ai still has the article).</div>;
    }
    return (
        <div className="mt-3" data-testid="dd-sources">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Sources ({sources.length}) — news, press releases, SEC filings, pipeline pages</div>
            <ul className="space-y-1">
                {sources.map((s) => (
                    <li key={s.source_record_id || s.url} className="text-xs flex items-start gap-2" data-testid={`dd-source-${s.source_record_id || s.url}`}>
                        <span className="font-mono text-slate-500 whitespace-nowrap">{fmtDate(s.published_at)}</span>
                        <SourceTypeBadge type={s.source_type || "news"} testId={`dd-source-type-${s.source_record_id || s.url}`} />
                        <a href={s.url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline inline-flex items-center gap-1 min-w-0">
                            <span className="truncate max-w-[420px]">{s.title || s.url.replace(/^https?:\/\//, "")}</span> <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                        </a>
                        {s.summary ? <span className="text-slate-500 truncate hidden lg:inline">— {s.summary}</span> : null}
                    </li>
                ))}
            </ul>
        </div>
    );
}
